// Carrier MBL # and booking # formats by carrier SCAC.
// Carriers not listed here are not checked.
//   mbl      → B/L (MBL #) patterns
//   booking  → Carrier Booking Reference # patterns (empty = not checked)
const MBL_RULES = {
    CMDU: { carrier: 'CMA CGM', mbl: [/^[A-Z]{3}[0-9]{7}[A-Z]?$/], booking: [/^[A-Z]{3}[0-9]{7}[A-Z]?$/], format: '3 letters + 7 digits, optional trailing letter', example: 'AMC2632361 / EDP0198979A' },
    ONEY: { carrier: 'ONE', mbl: [/^(ONEY)?[A-Z]{4}[A-Z0-9]{8}$/], booking: [/^[A-Z]{4}[A-Z0-9]{8}$/], format: 'optional ONEY + 4 letters + 8 letters/digits', bookingFormat: '4 letters + 8 letters/digits', example: 'MUMGA6818700' },
    MAEU: { carrier: 'Maersk', mbl: [/^[0-9]{9}$/], booking: [/^[0-9]{9}$/], format: '9 digits', example: '276755724' },
    HLCU: { carrier: 'Hapag-Lloyd', mbl: [/^HLCU[A-Z]{2}[A-Z0-9]{10}$/], booking: [/^[0-9]{8}$/], format: 'HLCU + 2 letters + 10 letters/digits', bookingFormat: '8 digits', example: 'HLCUDE1260945540', bookingExample: '37688231' },
    HDMU: { carrier: 'HMM', mbl: [/^[A-Z]{4}[0-9]{8}$/], booking: [/^[A-Z]{4}[0-9]{8}$/], format: '4 letters + 8 digits', example: 'BOMA70597000' },
    MSCU: { carrier: 'MSC', mbl: [/^MEDU[A-Z0-9]{8}$/], booking: [/^EBKG[0-9]{8}$/, /^[0-9]{3}[A-Z]{6}[0-9]{6}$/], format: 'MEDU + 8 letters/digits', bookingFormat: 'EBKG + 8 digits, or 3 digits + 6 letters + 6 digits', example: 'MEDUACD35826', bookingExample: 'EBKG18813076 / 177CZJZJS116127' },
    COSU: { carrier: 'COSCO', mbl: [/^(COSU|COAU)?[0-9]{10}$/], booking: [/^(COSU|COAU)?[0-9]{10}$/], format: 'optional COSU / COAU + 10 digits', example: 'COSU6508441430 / 6460721920' },
    SCIU: { carrier: 'SCI', mbl: [/^SCJU[A-Z0-9]{10}$/], booking: [/^[A-Z0-9]{3}BK[0-9]{9}$/], format: 'SCJU + 10 letters/digits', bookingFormat: '3 letters/digits + BK + 9 digits', example: 'SCJU1234567890', bookingExample: 'A39BK260007983' },
    TRKU: { carrier: 'Turkon', mbl: [/^(NSA|BOM)[0-9]{8}$/], booking: [], format: 'NSA or BOM + 8 digits', example: 'NSA26090050' },
    YMLU: { carrier: 'Yang Ming', mbl: [/^[A-Z][0-9]{9}$/], booking: [], format: '1 letter + 9 digits', example: 'N501000428' },
    PCIU: { carrier: 'PIL', mbl: [/^[A-Z]{3}[0-9]{9}$/], booking: [], format: '3 letters + 9 digits', example: 'BOM600414500' },
    BLJU: { carrier: 'Unifeeder', mbl: [/^[A-Z]{6}[0-9]{8}$/], booking: [/^[A-Z]{6}[0-9]{8}$/], format: '6 letters + 8 digits', example: 'INNSAD12345678' },
    EGLV: { carrier: 'Evergreen', mbl: [/^(EGLV)?[0-9]{12}$/], booking: [/^(EGLV)?[0-9]{12}$/], format: 'optional EGLV + 12 digits', example: 'EGLV123456789012' },
    IAAU: { carrier: 'InterAsia', mbl: [/^A[0-9]{2}[A-Z]{2}[0-9]{5}$/], booking: [/^A[0-9]{2}[A-Z]{2}[0-9]{5}$/], format: 'A + 2 digits + 2 letters + 5 digits', example: 'A26IN12345' },
}

// SCAC of a DSR carrier value: "Maersk (MAEU)" → MAEU. Falls back to the carrier list by display name.
export function carrierScac(carrier, carrierList = []) {
    const value = String(carrier ?? '').trim()
    if (!value) return ''
    const inParens = value.match(/\(([A-Za-z0-9]{2,8})\)\s*$/)
    if (inParens) return inParens[1].toUpperCase()
    const upper = value.toUpperCase()
    if (MBL_RULES[upper]) return upper
    const found = carrierList.find((c) => String(c?.['Display Name'] ?? '').trim().toUpperCase() === upper)
    return String(found?.['SCAC code'] ?? '').trim().toUpperCase()
}

/**
 * Checks the MBL # against the carrier's B/L format.
 * context: { bookingRef, hasContainer } from the DSR, for the carrier notes (Hapag-Lloyd, SCI).
 * Returns null when fine (or empty, or the carrier has no rule),
 * { level: 'error', message } to block the save, or { level: 'warning', message } to save with a warning.
 */
export function validateMbl(mbl, carrier, carrierList = [], context = {}) {
    const value = String(mbl ?? '').trim().toUpperCase()
    if (!value) return null
    const scac = carrierScac(carrier, carrierList)
    const rule = MBL_RULES[scac]
    if (!rule) return null
    if (!rule.mbl.some((p) => p.test(value))) {
        return { level: 'error', message: `Invalid MBL # "${value}" for ${rule.carrier} (${scac}). Expected ${rule.format}, e.g. ${rule.example}.` }
    }
    // Carrier notes for numbers in a valid format
    if (scac === 'HLCU' && !rule.booking.some((p) => p.test(String(context.bookingRef ?? '').trim()))) {
        return { level: 'warning', message: `Hapag-Lloyd B/L numbers are often not found by tracking. Please also fill the 8-digit booking number (Carrier Booking Reference #), e.g. 37688231.` }
    }
    if (scac === 'SCIU' && context.hasContainer === false) {
        return { level: 'warning', message: `SCI is tracked only by container number. Please add the container number.` }
    }
    return null
}

/**
 * Checks the Carrier Booking Reference # against the carrier's booking format.
 * Same result as validateMbl. Carriers without a booking format (Turkon, Yang Ming, PIL) are not checked.
 */
export function validateBookingRef(bookingRef, carrier, carrierList = []) {
    const value = String(bookingRef ?? '').trim().toUpperCase()
    if (!value) return null
    const scac = carrierScac(carrier, carrierList)
    const rule = MBL_RULES[scac]
    if (!rule || !rule.booking.length) return null
    if (rule.booking.some((p) => p.test(value))) return null
    return { level: 'error', message: `Invalid Carrier Booking Reference # "${value}" for ${rule.carrier} (${scac}). Expected ${rule.bookingFormat || rule.format}, e.g. ${rule.bookingExample || rule.example}.` }
}
